// @flow

import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import useDebouncedCallback from 'use-debounce/lib/useDebouncedCallback';
import { Header1, Markdown, WebformView } from '@catalytic/catalytic-ui';
import { makeStyles } from '@material-ui/styles';
import WebformError from './WebformError';
import WebformFields from './WebformFields';
import WebformFooter from './WebformFooter';
import WebformLastEdited from './WebformLastEdited';
import WebformLogo from './WebformLogo';
import WebformNotFound from './WebformNotFound';
import WebformSuccess from './WebformSuccess';
import { WebformProvider } from './WebformContext';
import ExpressionWebformContext from '../Expression/ExpressionWebformContext';
import ExpressionWebhookContext from '../Expression/ExpressionWebhookContext';
import { type Evaluate } from '../Expression/ExpressionContext';
import {
  fieldValueIsEmpty,
  getFieldInputs,
  getFileUrl
} from '../Field/FieldHelpers';
import {
  type Field as FieldType,
  type Inputs as InputsType
} from '../Field/FieldTypes';
import TopBarLoader from '../Loading/TopBarLoader';
import { WAIT as DEBOUNCE_WAIT } from '../const/debounce';
import Prompt from '../shared/Prompt/Prompt';
import HtmlTitle from '../shared/Helmet/HtmlTitle';
import { Favicon, type FaviconProps } from '../shared/Helmet/Favicon';
import getSessionStorageKey from '../utils/getSessionStorageKey';
import isUUID from '../utils/uuid';

const useStyles = makeStyles(theme => ({
  title: {
    marginBottom: '0.156rem'
  },
  description: {
    marginBottom: theme.functions.toRem(theme.variables.inputMarginBottom),
    ...theme.mixins.breakWord,
    hyphens: 'manual'
  },
  form: theme.mixins.formMarginBottom,
  textCenter: {
    textAlign: 'center'
  },
  timestamp: {
    ...theme.typography.extraSmallText,
    minHeight: '1.125rem',
    textAlign: 'right',
    color: theme.colors.battleshipGrey
  }
}));

type ExecuteWebhook = ({
  variables: { id: string, inputs: InputsType }
}) => mixed;

type Form = {
  description: ?string,
  displayName: ?string,
  formSubmitButtonText: ?string,
  formDescription: ?string,
  formFavicon: ?string,
  formLogo: ?string,
  formTitle: ?string,
  id: string,
  inputs: Array<FieldType>,
  name: ?string,
  processID: string
};

type Props = {
  executeWebhook: ExecuteWebhook,
  hideHeader: boolean,
  hideFooter: boolean,
  iconQueryResult: {
    data: {
      file?: { id: string } & FaviconProps
    },
    error: Error,
    loading: boolean
  },
  id?: string,
  isWebhook?: boolean,
  mutationResult: { data: Object, error: Error, loading: boolean },
  name?: string,
  params: URLSearchParams,
  teamQueryResult: {
    data: {
      team: {
        displayName: ?string,
        icon?: { id: string } & FaviconProps,
        thumbnail: ?{
          id: string,
          url: string
        }
      }
    },
    error: Error,
    loading: boolean
  },
  webformQueryResult: {
    data: {
      webform?: Form,
      webhook?: Form
    },
    error: Error,
    loading: boolean
  }
};

export const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (error) {
    return true;
  }
};

export const handleSubmit = ({
  evaluate,
  event,
  executeWebhook,
  formData,
  id,
  inputs,
  params,
  processID
}: {
  evaluate: Evaluate,
  event: SyntheticEvent<HTMLFormElement>,
  executeWebhook: ExecuteWebhook,
  formData: FormData,
  id: string,
  inputs: Array<FieldType>,
  params: URLSearchParams,
  processID: string
}) => {
  event.preventDefault();

  const data = getFieldInputs({
    formData,
    inputs,
    params,
    processID
  });

  // Prevent form submission if any required field values are empty
  const requiredFields = (inputs || []).filter((input: FieldType) => {
    const { condition, required } = input || {};

    // Conditional fields
    if (typeof condition === 'string' && evaluate(condition) === false) {
      return false;
    }

    // Required fields
    return (
      required &&
      data.some(({ id, value }) => input.id === id && fieldValueIsEmpty(value))
    );
  });

  if (requiredFields.length > 0) {
    return;
  }

  return executeWebhook({ variables: { id, inputs: data } });
};

export const Webform = ({
  executeWebhook,
  hideHeader,
  hideFooter,
  iconQueryResult,
  id: idProp,
  isWebhook,
  mutationResult,
  name: nameProp,
  params,
  teamQueryResult,
  webformQueryResult
}: Props) => {
  const [expressionContextUpdating, setExpressionContextUpdating] = useState(
    false
  );
  const classes = useStyles();
  const teamData = (teamQueryResult || {}).data;
  const { team } = teamData || {};
  const { displayName, icon: teamIcon, thumbnail } = team || {};
  const thumbnailUrl = thumbnail ? thumbnail.url : '';
  const iconData = (iconQueryResult || {}).data;
  const { file: formIcon } = iconData || {};
  const form = webformQueryResult?.data?.[isWebhook ? 'webhook' : 'webform'];
  const {
    description: descriptionProp,
    displayName: titleProp,
    formSubmitButtonText,
    formDescription,
    formFavicon,
    formLogo,
    formTitle,
    id: formId,
    inputs,
    name: formName,
    processID
  } = form || {};
  const description = formDescription || descriptionProp;
  const id = idProp || formId;
  const name = nameProp || formName;
  const title = formTitle || titleProp;
  const logoUrl =
    formLogo && typeof formLogo === 'string' && isUUID(formLogo)
      ? getFileUrl(formLogo)
      : formLogo && typeof formLogo === 'string'
      ? formLogo
      : thumbnailUrl;
  const { error: queryError } = webformQueryResult || {};
  const { data, error: mutationError, loading: mutationLoading } =
    mutationResult || {};
  const error = queryError || mutationError;
  const runID = data?.executeWebhook?.contextReference?.reference?.id;

  // Handle /form/:name and /webform/:id routes
  const ExpressionContext = isWebhook
    ? ExpressionWebhookContext
    : ExpressionWebformContext;
  const context = isWebhook ? id : name;
  const disableSubmit = !!(
    params &&
    params.get('disable-submit') &&
    params.get('disable-submit') === 'true'
  );

  // Save in-progress webforms
  const { pathname, search } = useLocation();
  const sessionStorageKey = getSessionStorageKey(`${pathname}${search}`);
  const isEmbedded = isInIframe();
  const [{ inputs: sessionValues, updatedAt }, setSessionValues] = useState(
    () => {
      if (!isEmbedded) {
        const data = window.localStorage.getItem(sessionStorageKey);

        if (data !== null) {
          try {
            return JSON.parse(data);
          } catch (error) {
            console.error(error);
          }
        }
      }

      return {
        inputs: [],
        updatedAt: ''
      };
    }
  );
  const formRef = useRef(null);
  function handleChange() {
    setExpressionContextUpdating(false);

    if (
      formRef &&
      typeof formRef === 'object' &&
      formRef.current &&
      typeof formRef.current === 'object'
    ) {
      const formData = new FormData(formRef.current);
      const data = {
        inputs: getFieldInputs({
          formData,
          inputs,
          params,
          processID
        }),
        updatedAt: new Date().toISOString()
      };

      if (!isEmbedded) {
        window.localStorage.setItem(sessionStorageKey, JSON.stringify(data));
      }

      setSessionValues(data);
    }
  }
  const [debouncedHandleChange] = useDebouncedCallback(
    handleChange,
    DEBOUNCE_WAIT
  );

  return (
    <div className={classNames({ [classes.textCenter]: data })}>
      {formFavicon &&
      typeof formFavicon === 'string' &&
      isUUID(formFavicon) &&
      formIcon ? (
        <Favicon
          appleTouchIcon={formIcon.appleTouchIcon}
          icon16={formIcon.icon16}
          icon32={formIcon.icon32}
          shortCutIcon={formIcon.shortCutIcon}
        />
      ) : formFavicon && typeof formFavicon === 'string' ? (
        <Favicon
          appleTouchIcon={formFavicon}
          icon16={formFavicon}
          icon32={formFavicon}
          shortCutIcon={formFavicon}
        />
      ) : teamIcon ? (
        <Favicon
          appleTouchIcon={teamIcon.appleTouchIcon}
          icon16={teamIcon.icon16}
          icon32={teamIcon.icon32}
          shortCutIcon={teamIcon.shortCutIcon}
        />
      ) : null}
      {mutationLoading && (
        <>
          <TopBarLoader />
          <Prompt when={mutationLoading} />
        </>
      )}
      {!formId ? (
        <WebformNotFound
          displayName={displayName}
          error={error}
          hideHeader={hideHeader}
          hideFooter={hideFooter}
          params={params}
          src={logoUrl}
        />
      ) : error ? (
        <WebformError
          displayName={displayName}
          error={error}
          hideHeader={hideHeader}
          hideFooter={hideFooter}
          params={params}
          src={logoUrl}
        />
      ) : data ? (
        <WebformSuccess
          data-testid="webform-success"
          displayName={displayName}
          hideHeader={hideHeader}
          hideFooter={hideFooter}
          params={params}
          runID={runID}
          sessionStorageKey={sessionStorageKey}
          src={logoUrl}
        />
      ) : (
        <>
          <WebformLastEdited
            isEmbedded={isEmbedded}
            sessionStorageKey={sessionStorageKey}
            updatedAt={updatedAt}
          />
          {!hideHeader && (
            <WebformLogo
              isEmbedded={isEmbedded}
              src={logoUrl}
              alt={displayName || ''}
            />
          )}
          {title && (
            <>
              <HtmlTitle>{title}</HtmlTitle>
              <Header1 className={classes.title} data-testid="webform-header">
                {title}
              </Header1>
            </>
          )}
          {description && (
            <div
              className={classes.description}
              data-testid="webform-description"
            >
              <Markdown value={description} />
            </div>
          )}
          {context && (
            <ExpressionContext context={context}>
              {expressionContextResult => {
                const { evaluate, loading: expressionContextLoading, update } =
                  expressionContextResult || {};

                return (
                  <WebformView
                    className={classes.form}
                    disabled={!!error || expressionContextLoading}
                    hasSubmit={!disableSubmit}
                    loading={mutationLoading || expressionContextUpdating}
                    onSubmit={(
                      event: SyntheticEvent<HTMLFormElement>,
                      formData: FormData
                    ) =>
                      handleSubmit({
                        evaluate,
                        event,
                        executeWebhook,
                        formData,
                        id,
                        inputs,
                        params,
                        processID
                      })
                    }
                    ref={formRef}
                    submit={formSubmitButtonText}
                  >
                    <WebformProvider value={{ webhookID: id }}>
                      <WebformFields
                        evaluate={evaluate}
                        inputs={inputs}
                        loading={expressionContextLoading}
                        onChange={event => {
                          if (!expressionContextUpdating) {
                            setExpressionContextUpdating(true);
                          }

                          debouncedHandleChange(event);
                        }}
                        params={params}
                        sessionValues={sessionValues}
                        update={update}
                      />
                    </WebformProvider>
                  </WebformView>
                );
              }}
            </ExpressionContext>
          )}
        </>
      )}
      {!hideFooter && <WebformFooter />}
    </div>
  );
};
Webform.displayName = 'Webform';

export default Webform;
